import { useState } from "react";
import { Save, X, Trash2, Pencil, GripVertical } from "lucide-react";
import CustomCheckbox from "./CustomCheckbox";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import Modal from "./Modal";

const getMilestoneId = (milestone) => milestone.id || milestone.tmpId;

// Sortable row component
const SortableRow = ({
  milestone,
  editMode,
  editData,
  setEditData,
  onSave,
  onCancel,
  onDelete,
  onEdit,
  onValidatingChange,
  index,
  onShowDeleteModal,
  showSelectedColumn,
  onSelectChange,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: getMilestoneId(milestone),
      disabled: !editMode,
    });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleInputChange = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [getMilestoneId(milestone)]: { 
        ...prev[getMilestoneId(milestone)], 
        [field]: value,
      },
    }));
  };

  // Icon styles for the square background design
  const iconContainerStyle =
    "flex items-center justify-center w-6 h-6 bg-gray-800 text-white rounded cursor-pointer";
  // Determine row background color based on index (even/odd)
  const rowBgClass = index % 2 === 0 ? "bg-tableau-bg" : "bg-tableau-bg1";
  // Add a subtle left border when in edit mode to indicate the row is being edited
  const editIndicatorClass = editMode ? "border-l-4 border-blue-400" : "";

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`${rowBgClass} ${editIndicatorClass}`}
    >
      {/* Drag handle - only visible in edit mode */}
      <td className="py-3 px-2 w-10">
        {editMode && (
          <div {...attributes} {...listeners} className="cursor-grab">
            <GripVertical className="h-5 w-5 text-gray-400" />
          </div>
        )}
      </td>
      {/* Milestone number */}
      <td className="py-3 px-4 w-20 text-center">{milestone.number}</td>
      {/* Milestone name */}
      <td className="py-3 px-4">
        {editMode ? (
          <input
            type="text"
            value={editData[getMilestoneId(milestone)]?.name || ""} 
            onChange={(e) => handleInputChange("name", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#5B93FF]"
          />
        ) : (
          milestone.name || "[Milestone name]"
        )}
      </td>
      {/* Milestone description */}
      <td className="py-3 px-4">
        {editMode ? (
          <input
            type="text"
            value={editData[getMilestoneId(milestone)]?.description || ""} 
            onChange={(e) => handleInputChange("description", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#5B93FF]"
          />
        ) : (
          milestone.description ||
          "[Milestone description Milestone description]"
        )}
      </td>
      {/* Validating milestone */}
      <td className="py-3 px-4 text-center">
        <CustomCheckbox
          checked={
            editMode ? editData[getMilestoneId(milestone)]?.mandatory : milestone.mandatory 
          }
          onChange={(isChecked) => {
            if (editMode) {
              handleInputChange("mandatory", isChecked);
            } else {
              onValidatingChange(getMilestoneId(milestone), isChecked); 
            }
          }}
          disabled={!editMode}
        />
      </td>
      {showSelectedColumn && (
        <td className="py-3 px-4 text-center">
          <CustomCheckbox
            checked={
              editMode ? editData[getMilestoneId(milestone)]?.selected : milestone.selected 
            }
            onChange={(isChecked) => {
              if (editMode) {
                handleInputChange("selected", isChecked);
              } else {
                onSelectChange?.(getMilestoneId(milestone), isChecked); 
              }
            }}
            disabled={!editMode}
          />
        </td>
      )}
      {/* Actions */}
      <td className="py-3 px-2 text-right">
        <div className="flex space-x-1 justify-end">
          {editMode ? (
            <>
              <div onClick={onCancel}>
                <img
                  src="/icons/study/Cancel.svg"
                  alt="Cancel"
                  className="h-10 w-10"
                />
              </div>
              <div onClick={onSave}>
                <img
                  src="/icons/study/Save.svg"
                  alt="Save"
                  className="h-10 w-10"
                />
              </div>
              <div onClick={() => onShowDeleteModal(milestone)}>
                <img
                  src="/icons/study/Bin.svg"
                  alt="Bin"
                  className="h-10 w-10"
                />
              </div>
            </>
          ) : (
            <>
              <button
                onClick={onEdit}
                className="text-gray-700 hover:text-gray-900"
                aria-label="Edit"
              >
                <img
                  src="/icons/study/Modifie.svg"
                  alt="Edit"
                  className="h-4 w-4"
                />
              </button>
              <button
                onClick={() => onShowDeleteModal(milestone)}
                className="text-gray-700 hover:text-gray-900"
                aria-label="Delete"
              >
                <img
                  src="/icons/study/Bin.svg"
                  alt="Cancel"
                  className="h-4 w-4"
                />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

// Regular row component for non-edit mode
const RegularRow = ({
  milestone,
  editMode,
  onEdit,
  onDelete,
  onValidatingChange,
  index,
  onShowDeleteModal,
  showSelectedColumn,
  onSelectChange,
}) => {
  // Determine row background color based on index (even/odd)
  const rowBgClass = index % 2 === 0 ? "bg-tableau-bg1" : "bg-tableau-bg";
  return (
    <tr className={rowBgClass}>
      {/* Empty cell where drag handle would be */}
      <td className="py-3 px-2 w-10"></td>
      {/* Milestone number */}
      <td className="py-3 px-4 w-20 text-center">{milestone.number}</td>
      {/* Milestone name */}
      <td className="py-3 px-4">{milestone.name || "[Milestone name]"}</td>
      {/* Milestone description */}
      <td className="py-3 px-4">
        {milestone.description ||
          "[Milestone description Milestone description]"}
      </td>
      {/* Validating milestone */}
      <td className="py-3 px-4 text-center">
        <CustomCheckbox
          checked={milestone.mandatory}
          onChange={(isChecked) => onValidatingChange(getMilestoneId(milestone), isChecked)}
        />
      </td>

      {showSelectedColumn && (
        <td className="py-3 px-4 text-center">
          <CustomCheckbox
            checked={milestone.selected || false}
            onChange={(isChecked) => onSelectChange?.(getMilestoneId(milestone), isChecked)} 
            disabled={!editMode}
          />
        </td>
      )}
      {/* Actions */}
      <td className="py-3 px-4 text-right">
        <div className="flex space-x-2 justify-end">
          <button
            onClick={onEdit}
            className="text-gray-700 hover:text-gray-900"
            aria-label="Edit"
          >
            <img
              src="/icons/study/Modifie.svg"
              alt="Edit"
              className="h-6 w-6"
            />
          </button>
          <button
            onClick={() => onShowDeleteModal(milestone)}
            className="text-gray-700 hover:text-gray-900"
            aria-label="Delete"
          >
            <img src="/icons/study/Bin.svg" alt="Cancel" className="h-6 w-6" />
          </button>
        </div>
      </td>
    </tr>
  );
};

export default function MilestoneTable({
  milestones = [],
  onEdit = null,
  onDelete = null,
  onValidatingChange = null,
  onReorder = null,
  onSelectChange = null,
  showSelectedColumn = false,
  onEditStart = () => {},
  onEditEnd = () => {},
  emptyMessage = "No milestones have been recorded yet. They will appear in the table once added",
}) {
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  // Modal states
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [milestoneToDelete, setMilestoneToDelete] = useState(null);
  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  // Handle edit button click - enable edit mode for all milestones
  const handleEdit = () => {
    // Initialize edit data for all milestones
    const initialEditData = {};
    milestones.forEach((milestone) => {
      initialEditData[getMilestoneId(milestone)] = { 
        name: milestone.name,
        description: milestone.description,
        mandatory: milestone.mandatory,
        selected: milestone.selected,
      };
    });
    setEditData(initialEditData);
    setEditMode(true);
    onEditStart(); 
  };

  // Handle save button click - show confirmation modal
  const handleSaveClick = () => {
    setShowSaveModal(true);
  };

  // Handle cancel button click - show confirmation modal
  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  // Handle delete button click - show confirmation modal
  const handleDeleteClick = (milestone) => {
    setMilestoneToDelete(milestone);
    setShowDeleteModal(true);
  };

  // Handle save confirmation
  const handleSaveConfirm = () => {
    // Update all milestones with edited data
    const updatedMilestones = milestones.map((milestone) => ({
      ...milestone,
      name: editData[getMilestoneId(milestone)]?.name || milestone.name, 
      description: editData[getMilestoneId(milestone)]?.description || milestone.description,
      mandatory: editData[getMilestoneId(milestone)]?.mandatory ?? milestone.mandatory, 
      selected: editData[getMilestoneId(milestone)]?.selected ?? milestone.selected, 
    }));
    if (onEdit) {
      if (typeof onEdit === "function") {
        updatedMilestones.forEach((milestone) => onEdit(milestone));
      }
      if (typeof onEdit === "object" && onEdit.multiple) {
       
        onEdit.multiple(updatedMilestones);
      }
    }
    setEditMode(false);
    setShowSaveModal(false);
    onEditEnd(); 
  };

  // Handle cancel confirmation
  const handleCancelConfirm = () => {
    setEditMode(false);
    setShowCancelModal(false);
    onEditEnd(); 
  };

  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    if (onDelete && milestoneToDelete) {
      onDelete(milestoneToDelete);
    }
    setShowDeleteModal(false);
    setMilestoneToDelete(null);
  };

  // Handle DnD end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id && over) {
      // Use active.id and over.id
      const oldIndex = milestones.findIndex((m) => getMilestoneId(m) === active.id); 
      const newIndex = milestones.findIndex((m) => getMilestoneId(m) === over.id); 
      if (onReorder && typeof onReorder === "function") {
        onReorder(oldIndex, newIndex);
      }
    }
  };

  // Check if data is empty
  const isEmpty = milestones.length === 0;

  // Render table content based on edit mode
  const renderTableContent = () => {
    if (isEmpty) {
      return (
        <tr>
          <td
            colSpan={showSelectedColumn ? 7 : 6}
            className="text-center py-6 bg-tableau-bg1"
          >
            {emptyMessage}
          </td>
        </tr>
      );
    }
    if (editMode) {
      // In edit mode, use DndContext for drag and drop
      return (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={milestones.map((m) => getMilestoneId(m))} 
            strategy={verticalListSortingStrategy}
          >
            {" "}
            {/* Use m.id */}
            {milestones.map((milestone, index) => (
              <SortableRow
                key={getMilestoneId(milestone)} 
                milestone={milestone}
                editMode={editMode}
                editData={editData}
                setEditData={setEditData}
                onSave={handleSaveClick}
                onCancel={handleCancelClick}
                onDelete={onDelete}
                onEdit={handleEdit}
                onValidatingChange={onValidatingChange}
                index={index}
                onShowDeleteModal={handleDeleteClick}
                showSelectedColumn={showSelectedColumn}
                onSelectChange={onSelectChange}
              />
            ))}
          </SortableContext>
        </DndContext>
      );
    } else {
      // In normal mode, use regular rows without drag and drop
      return milestones.map((milestone, index) => (
        <RegularRow
          key={getMilestoneId(milestone)}
          milestone={milestone}
          onEdit={handleEdit}
          onDelete={onDelete}
          onValidatingChange={onValidatingChange}
          index={index}
          onShowDeleteModal={handleDeleteClick}
          showSelectedColumn={showSelectedColumn}
          onSelectChange={onSelectChange}
        />
      ));
    }
  };

  return (
    <div className="bg-tableau-bg rounded-xl overflow-hidden shadow-sm border border-tableau-border">
      <table className="w-full border-collapse">
        <thead className="bg-tableau-bg border-b border-tableau-border">
          <tr className="text-left">
            <th className="py-3 px-2 w-10"></th>
            <th className="py-3 px-4 font-medium text-gray-700 w-20 text-center">
              Milestone&nbsp;#
            </th>
            <th className="py-3 px-4 font-medium text-gray-700">
              Milestone name
            </th>
            <th className="py-3 px-4 font-medium text-gray-700">
              Milestone description
            </th>
            <th className="py-3 px-4 font-medium text-gray-700 text-center">
              Validating milestone
            </th>
            {showSelectedColumn && (
              <th className="py-3 px-4 font-medium text-gray-700 text-center">
                Selected
              </th>
            )}
            <th className="py-3 px-4 w-24">Actions</th>
          </tr>
        </thead>
        <tbody className="text-gray-500 font-normal">
          {renderTableContent()}
        </tbody>
      </table>
      {/* Save Confirmation Modal */}
      <Modal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        title="Milestone changes"
        primaryAction={handleSaveConfirm}
        primaryActionLabel="Save Changes"
        secondaryAction={() => setShowSaveModal(false)}
        secondaryActionLabel="Back"
      >
        <p className="mb-4">
          You are about to save the modifications made to the milestones. These
          changes will impact the clinical study and will be visible to all
          users.
        </p>
        <p className="mb-4">
          Once saved, these modifications will be applied to the study and may
          affect the workflow of investigators.
        </p>
        <p className="font-medium">Do you want to continue?</p>
      </Modal>
      {/* Cancel Confirmation Modal */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Discard changes"
        primaryAction={handleCancelConfirm}
        primaryActionLabel="Discard Changes"
        secondaryAction={() => setShowCancelModal(false)}
        secondaryActionLabel="Back"
      >
        <p className="mb-4">
          You are about to discard all modifications made to the milestones.
        </p>
        <p className="mb-4">
          This is destructive and means all your before editing.
        </p>
        <p className="font-medium">Are you sure you want to proceed?</p>
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete milestone"
        primaryAction={handleDeleteConfirm}
        primaryActionLabel="Delete milestone"
        secondaryAction={() => setShowDeleteModal(false)}
        secondaryActionLabel="Back"
      >
        <p className="mb-4">
          You are about to permanently delete the milestone.
        </p>
        <p className="mb-4">
          Once you remove this milestone and the associated actions.
        </p>
        <p className="font-medium">Are you sure you want to proceed?</p>
      </Modal>
    </div>
  );
}